/**
 * The interactive Leadership report's queries (12 Oct): the page's numbers for
 * a range and filters, the tickets behind one number, and the Excel download.
 * Every route answers 403 to anyone who is not a report viewer.
 */

import { keepPreviousData, useQuery } from "@tanstack/react-query";

import {
  exportReportInsights as sdkExportReportInsights,
  getReportInsights as sdkGetReportInsights,
  type InsightsMeasure,
  type InsightsResponse,
  type InsightsTicketListResponse,
  listReportInsightTickets as sdkListReportInsightTickets,
} from "@/lib/api";
import { logger } from "@/lib/logger";

import { leadershipReportKeys } from "./api";
import { apiQuery, type InsightsState } from "./insights-helpers";

export interface Drill {
  measure: InsightsMeasure;
  value: string | null;
  /** What the drawer's title says (already translated). */
  title: string;
}

const insightsKeys = {
  page: (name: string, query: string) =>
    [...leadershipReportKeys.all, name, "insights", query] as const,
  tickets: (name: string, query: string, measure: string, value: string | null) =>
    [...leadershipReportKeys.all, name, "insights", query, "tickets", measure, value] as const,
};

/**
 * The page's numbers. Refetched every 5 minutes (the data changes three times
 * a day, or on "Update now"); the previous numbers stay on screen while a new
 * range or filter loads.
 */
export function useReportInsights(name: string, state: InsightsState) {
  const query = apiQuery(state);
  return useQuery({
    queryKey: insightsKeys.page(name, JSON.stringify(query)),
    queryFn: async (): Promise<InsightsResponse> => {
      const { data } = await sdkGetReportInsights({ path: { name }, query, throwOnError: true });
      return data;
    },
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    retry: (count, error) => count < 2 && (error as { status?: number })?.status !== 404,
  });
}

/** The tickets behind one number (the drill-in drawer); off while no number is open. */
export function useInsightTickets(name: string, state: InsightsState, drill: Drill | null) {
  const query = apiQuery(state);
  return useQuery({
    queryKey: insightsKeys.tickets(
      name,
      JSON.stringify(query),
      drill?.measure ?? "",
      drill?.value ?? null,
    ),
    queryFn: async (): Promise<InsightsTicketListResponse> => {
      const { data } = await sdkListReportInsightTickets({
        path: { name },
        query: { ...query, measure: drill!.measure, value: drill!.value ?? undefined },
        throwOnError: true,
      });
      return data;
    },
    enabled: drill !== null,
    staleTime: 60_000,
  });
}

/** Save `blob` as `filename` through a temporary link. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** "Download Excel": the server builds the workbook for the current range and filters. */
export async function downloadInsightsExcel(name: string, state: InsightsState): Promise<void> {
  const { data, response } = await sdkExportReportInsights({
    path: { name },
    query: apiQuery(state),
    parseAs: "blob",
    throwOnError: true,
  });
  const disposition = response.headers.get("content-disposition") ?? "";
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? "helpdesk-leadership.xlsx";
  logger.debug("[leadership-report] Excel downloaded", filename);
  saveBlob(data as unknown as Blob, filename);
}
